import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type {
  McpServer,
  McpTransport,
  Team,
} from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { McpPermissionsTab } from "@/features/mcps/components/McpPermissionsTab";
import { McpToolsTab } from "@/features/mcps/components/McpToolsTab";
import {
  useCreateMcp,
  useUpdateMcp,
} from "@/features/mcps/hooks/useMcps";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mcp?: McpServer | null;
}

const TRANSPORTS: { value: McpTransport; label: string; disabled?: boolean }[] = [
  { value: "http", label: "HTTP (streamable-http, recomendado)" },
  { value: "sse", label: "SSE (deprecado pela spec MCP — prefira HTTP)" },
  {
    value: "stdio",
    label: "stdio (bloqueado por segurança)",
    disabled: true,
  },
];

const PRESERVE_PLACEHOLDER = '{ "_existing": "•••" }';

export function McpFormDialog({ open, onOpenChange, mcp }: Props) {
  const { t } = useTranslation("common");
  const { t: tm } = useTranslation("mcps");
  const isEdit = Boolean(mcp);
  const create = useCreateMcp();
  const update = useUpdateMcp(mcp?.id ?? "");
  const { data: teams } = useTeams({ include_inactive: false });
  const { isAdmin, memberships } = useAuth();

  // Filtro de times elegíveis (manager OR creator do time -- alinha
  // com policy do backend MCP_SERVER_CREATED).
  const eligibleTeams = useMemo<Team[]>(() => {
    if (!teams) return [];
    if (isAdmin) return teams;
    const elevatedIds = new Set(
      memberships
        .filter((m) => m.role === "manager" || m.role === "creator")
        .map((m) => m.team.id),
    );
    return teams.filter((t) => elevatedIds.has(t.id));
  }, [teams, isAdmin, memberships]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerTeamId, setOwnerTeamId] = useState("");
  const [transport, setTransport] = useState<McpTransport>("http");
  const [url, setUrl] = useState("");
  const [credentialsText, setCredentialsText] = useState("");
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  // ``undefined`` = user nao tocou a whitelist nesta sessao do dialog.
  // ``string[]`` = mudou; sera enviado no PATCH global do "Salvar".
  const [enabledToolsDraft, setEnabledToolsDraft] = useState<
    string[] | undefined
  >(undefined);

  useEffect(() => {
    if (open && mcp) {
      setName(mcp.name);
      setDescription(mcp.description ?? "");
      setOwnerTeamId(mcp.owner_team_id);
      setTransport(mcp.transport);
      setUrl(mcp.url ?? "");
      setCredentialsText(mcp.has_credentials ? PRESERVE_PLACEHOLDER : "");
      setCredentialsError(null);
      setEnabledToolsDraft(undefined);
    } else if (open) {
      setName("");
      setDescription("");
      setOwnerTeamId(eligibleTeams[0]?.id ?? "");
      setTransport("http");
      setUrl("");
      setCredentialsText("");
      setCredentialsError(null);
      setEnabledToolsDraft(undefined);
    }
  }, [open, mcp, eligibleTeams]);

  function parseCredentials(): Record<string, unknown> | undefined {
    const trimmed = credentialsText.trim();
    if (!trimmed) return undefined; // preserva atual
    if (trimmed === PRESERVE_PLACEHOLDER) return undefined; // preserva
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("JSON deve ser objeto");
      }
      return parsed as Record<string, unknown>;
    } catch (err) {
      setCredentialsError((err as Error).message);
      throw err;
    }
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setCredentialsError(null);
    try {
      const credentials = parseCredentials();
      if (isEdit && mcp) {
        await update.mutateAsync({
          name,
          description: description || undefined,
          url: url || undefined,
          credentials,
          enabled_tools: enabledToolsDraft,
        });
        toast.success(tm("form.toasts.updated"));
      } else {
        if (!ownerTeamId) {
          toast.error(tm("form.toasts.selectOwnerTeam"));
          return;
        }
        await create.mutateAsync({
          name,
          description: description || undefined,
          owner_team_id: ownerTeamId,
          transport,
          url: url || undefined,
          credentials,
        });
        toast.success(tm("form.toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      // credentialsError ja foi setado se foi parse
      if (credentialsError) return;
      toastApiError(err);
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? tm("form.titleEdit") : tm("form.titleNew")}
          </DialogTitle>
          <DialogDescription>{tm("form.description")}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="transport">
          <TabsList>
            <TabsTrigger value="transport">
              {tm("form.tabs.transport")}
            </TabsTrigger>
            <TabsTrigger value="credentials">
              {tm("form.tabs.credentials")}
            </TabsTrigger>
            {isEdit ? (
              <TabsTrigger value="permissions">
                {tm("form.tabs.permissions")}
              </TabsTrigger>
            ) : null}
            {isEdit ? (
              <TabsTrigger value="tools">{tm("form.tabs.tools")}</TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="transport">
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="mcp-name">{tm("form.labels.name")}</Label>
                <Input
                  id="mcp-name"
                  required
                  minLength={2}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mcp-desc">
                  {tm("form.labels.description")}
                </Label>
                <Textarea
                  id="mcp-desc"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              {!isEdit ? (
                <div className="space-y-2">
                  <Label htmlFor="mcp-owner">
                    {tm("form.labels.ownerTeam")}
                  </Label>
                  {eligibleTeams.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {tm("form.labels.noEligibleTeam")}
                    </p>
                  ) : (
                    <select
                      id="mcp-owner"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={ownerTeamId}
                      onChange={(e) => setOwnerTeamId(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        {tm("form.labels.select")}
                      </option>
                      {eligibleTeams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : null}
              {!isEdit ? (
                <div className="space-y-2">
                  <Label htmlFor="mcp-transport">
                    {tm("form.labels.transport")}
                  </Label>
                  <select
                    id="mcp-transport"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={transport}
                    onChange={(e) => setTransport(e.target.value as McpTransport)}
                  >
                    {TRANSPORTS.map((t) => (
                      <option key={t.value} value={t.value} disabled={t.disabled}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-xs text-muted-foreground underline decoration-dotted">
                          {tm("form.labels.stdioBlocked")}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>
                        {tm("form.labels.stdioBlockedTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="mcp-url">{tm("form.labels.url")}</Label>
                <Input
                  id="mcp-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required={transport === "http" || transport === "sse"}
                  placeholder="https://mcp.example.com/sse"
                />
              </div>
            </form>
          </TabsContent>

          <TabsContent value="credentials">
            <div className="space-y-2 py-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="mcp-creds">
                  {tm("form.labels.credentialsJson")}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={tm("form.labels.credentialsHelpAria")}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground text-[10px] font-bold text-muted-foreground hover:bg-muted"
                      >
                        ?
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md space-y-2">
                      <p className="text-xs font-semibold">
                        {tm("form.credsHelp.title")}
                      </p>
                      <p className="text-xs">{tm("form.credsHelp.intro")}</p>
                      <div className="space-y-1 text-xs">
                        <p className="font-semibold">
                          {tm("form.credsHelp.bearerTitle")}
                        </p>
                        <pre className="rounded bg-muted/50 p-2 font-mono">
{`{ "bearer": "seu-token-aqui" }`}
                        </pre>
                        <p
                          className="text-muted-foreground"
                          dangerouslySetInnerHTML={{
                            __html: tm("form.credsHelp.bearerNote"),
                          }}
                        />
                      </div>
                      <div className="space-y-1 text-xs">
                        <p className="font-semibold">
                          {tm("form.credsHelp.headersTitle")}
                        </p>
                        <pre className="rounded bg-muted/50 p-2 font-mono">
{`{
  "headers": {
    "X-Api-Key": "abc123",
    "X-Tenant": "orbit"
  }
}`}
                        </pre>
                        <p
                          className="text-muted-foreground"
                          dangerouslySetInnerHTML={{
                            __html: tm("form.credsHelp.headersNote"),
                          }}
                        />
                      </div>
                      <div className="space-y-1 text-xs">
                        <p className="font-semibold">
                          {tm("form.credsHelp.noAuthTitle")}
                        </p>
                        <pre className="rounded bg-muted/50 p-2 font-mono">
{`{}`}
                        </pre>
                        <p className="text-muted-foreground">
                          {tm("form.credsHelp.noAuthNote")}
                        </p>
                      </div>
                      <p
                        className="text-xs text-muted-foreground"
                        dangerouslySetInnerHTML={{
                          __html: tm("form.credsHelp.encryptedNote"),
                        }}
                      />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Textarea
                id="mcp-creds"
                rows={6}
                value={credentialsText}
                onChange={(e) => {
                  setCredentialsText(e.target.value);
                  setCredentialsError(null);
                }}
                placeholder={tm("form.labels.credentialsPlaceholder")}
                className="font-mono text-xs"
              />
              {credentialsError ? (
                <p className="text-sm text-destructive">{credentialsError}</p>
              ) : null}
              {isEdit ? (
                <p
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: tm("form.labels.credentialsEditHint", {
                      preserve: PRESERVE_PLACEHOLDER,
                    }),
                  }}
                />
              ) : (
                <p
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: tm("form.labels.credentialsNewHint"),
                  }}
                />
              )}
            </div>
          </TabsContent>

          {isEdit && mcp ? (
            <TabsContent value="permissions">
              <McpPermissionsTab mcp={mcp} />
            </TabsContent>
          ) : null}

          {isEdit && mcp ? (
            <TabsContent value="tools">
              <McpToolsTab
                mcp={mcp}
                onEnabledToolsChange={setEnabledToolsDraft}
              />
            </TabsContent>
          ) : null}
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("actions.close")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pending || name.length < 2}
          >
            {isEdit ? t("actions.save") : t("actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
